// "Ideas get in from wherever you are" (v1.78.0): a reel shared straight from
// Instagram lands as a small bar — pick the project, one tap, it's a card.
// Reads the boot stash (main.tsx); disappears forever on create or dismiss.
import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '@/core/api/apiClient';
import { Icon } from '@/core/ui/icons';
import { useToastStore } from '@/core/ui/toastStore';
import { useAreaStore } from '@/features/areas/store/areaStore';
import type { TaskDetail } from '@cg-projects/shared';

interface Share { title: string; text: string; url: string }

function readStash(): Share | null {
  try {
    const raw = sessionStorage.getItem('cgp:share');
    if (!raw) return null;
    const s = JSON.parse(raw) as Share;
    return s.url || s.text || s.title ? s : null;
  } catch { return null; }
}

export function ShareCapture() {
  const [share, setShare] = useState<Share | null>(readStash);
  const [busy, setBusy] = useState(false);
  const toast = useToastStore((s) => s.show);
  const areas = useAreaStore((s) => s.areas);
  // Content teams first (that's where reels go); everything else still offered.
  const options = useMemo(() => [...areas].sort((a, b) => Number(b.isContentTeam) - Number(a.isContentTeam)), [areas]);
  const [areaId, setAreaId] = useState<number | ''>('');

  if (!share) return null;
  const link = share.url || (/(https?:\/\/\S+)/.exec(share.text)?.[1] ?? '');
  const dismiss = () => { try { sessionStorage.removeItem('cgp:share'); } catch { /* gone */ } setShare(null); };

  const create = async () => {
    const aid = areaId || options.find((a) => a.isContentTeam)?.id || options[0]?.id;
    if (!aid || busy) return;
    setBusy(true);
    try {
      const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const parts: string[] = [];
      if (link) parts.push(`<p><a href="${esc(link)}">${esc(link)}</a></p>`);
      if (share.text && share.text !== link) parts.push(`<p>${esc(share.text)}</p>`);
      const { task, similar } = await api.post<{ task: TaskDetail; similar?: { id: number; name: string } | null }>('/tasks', {
        areaId: aid,
        name: (share.title || 'New reel idea').slice(0, 140),
        descriptionHtml: parts.join('') || undefined,
      });
      toast(similar
        ? `Card created — heads up, "${similar.name}" already has this reel.`
        : `Card created: "${task.name}".`, 'info');
      dismiss();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not create the card.', 'error');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-x-0 bottom-4 z-[60] mx-auto flex w-[min(560px,94vw)] flex-wrap items-center gap-2 rounded-lg border border-brand/50 bg-canvas px-3 py-2 shadow-xl animate-fade-in">
      <span className="min-w-0 flex-1 truncate text-[13px] text-ink" title={link || share.title}>
        📥 {share.title || link || 'Shared item'}
      </span>
      <select value={areaId} onChange={(e) => setAreaId(e.target.value ? Number(e.target.value) : '')}
        className="h-7 max-w-[160px] rounded-md border border-line bg-canvas px-1.5 text-xs text-ink outline-none">
        <option value="">{options.find((a) => a.isContentTeam)?.name ?? 'Pick a project'}</option>
        {options.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
      <button onClick={() => void create()} disabled={busy || options.length === 0}
        className="rounded-md bg-brand px-3 py-1 text-xs font-semibold text-page transition hover:brightness-110 disabled:opacity-50">
        {busy ? 'Creating…' : 'Make it a card'}
      </button>
      <button onClick={dismiss} className="rounded p-1 text-faint hover:text-ink" title="Not now"><Icon i={X} size={14} /></button>
    </div>
  );
}
